import { GridColDef, GridColumnVisibilityModel, GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid"
import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import Big from "big.js"
import {
  InvoiceExceptionsHistoryRead,
  InvoiceType,
  UiEditContainer,
} from "./api/generated"
import { RootState } from "./app/store"
import { gridColumns as invoiceTableGridColumnsFromSchema } from "./components/InvoicesTable/InvoicesTable.schema"
import { tempColumns as poLinksTableGridColumnsFromSchema } from "./components/PurchaseOrderLinks/PurchaseOrderLinks.schema"

export interface SupplierSite {
  id: number
  name: string
}

export interface SearchState {
  selectedColumn: string;
  searchQuery: string;
  dropdownSelected: boolean;
  dateFilters: {
    invoiceDate: string;
    glDate: string;
  };
}

export interface EditingInvoiceState {
  lineItems: UiEditContainer[]
  opcoSlug: string
  rowSelectionModel: GridRowSelectionModel
  purchaseOrderRowSelectionCount: number
  purchaseOrderRowSelectionSum: number
  purchaseOrderRowSelectionMismatch: number
  assignedUser: string
  invoiceId: number
  invoiceType: InvoiceType
  externalInvoiceId: string
  receiverNameInvoice: string
  supplierSite?: SupplierSite
  supplierSiteId?: number
  supplierSiteOptions: SupplierSite[]
  poNumber: string
  currency: string
  grossAmount?: number
  taxAmount?: number | null
  totalLineAmount?: number
  businessUnit: string
  receiverNamePO: string
  invoiceDate: string
  glDate: string
  paymentTerms: string
  paymentMethod: string
  payGroup: string
  bankDetailsString: string
  note: string
  validationErrors: InvoiceExceptionsHistoryRead[]
  supplierName?: string
  supplierNumber?: string
  supplierId?: number
  invoiceSupplierName?: string
  showInvoiceInline: boolean
  supplierBankDetailsId: number
  supplierIban: string
  supplierBicSwift: string
  supplierBankAccountNumber: string
  supplierSortCode: string
  qrIban: string
  qrIbanReference: string
  isQrr: boolean,
  spotRates: (Record<string, string> | null),
  nonPoRequester: string
  nonPoAttribute: string
  hydrated: boolean
  invoice_user_flag: number
  isNonPoValidationRuleEnabled: boolean
}

const EnteringInvoiceInformationDefaultState: EditingInvoiceState = {
  invoiceType: undefined,
  lineItems: [],
  opcoSlug: "",
  rowSelectionModel: [],
  purchaseOrderRowSelectionCount: 0,
  purchaseOrderRowSelectionSum: 0,
  purchaseOrderRowSelectionMismatch: 0,
  assignedUser: "",
  invoiceId: 0,
  externalInvoiceId: "",
  supplierSite: undefined,
  supplierSiteId: undefined,
  supplierSiteOptions: [],
  poNumber: "",
  currency: "",
  grossAmount: undefined,
  taxAmount: undefined,
  totalLineAmount: undefined,
  businessUnit: "",
  receiverNamePO: "",
  receiverNameInvoice: "",
  invoiceDate: "",
  glDate: "",
  paymentTerms: "",
  paymentMethod: "",
  payGroup: "",
  bankDetailsString: "",
  invoice_user_flag: undefined,
  note: "",
  validationErrors: [],
  supplierName: "",
  supplierNumber: "",
  supplierId: undefined,
  invoiceSupplierName: "",
  showInvoiceInline: true,
  supplierBankDetailsId: undefined,
  supplierIban: "",
  supplierBicSwift: "",
  supplierBankAccountNumber: "",
  supplierSortCode: "",
  qrIban: "",
  qrIbanReference: "",
  isQrr: false,
  spotRates: null,
  nonPoRequester: "",
  nonPoAttribute: "",
  hydrated: false,
  isNonPoValidationRuleEnabled:false
}

export interface CopyPasteArgs {
  source: string
  destination: string
}

export interface ActiveScreenState {
  selectedInvoice?: number;
  enteredInvoiceInformation: EditingInvoiceState;
  activeScreen: "login" | "landing" | "invoices" | "invoiceDetail" | "settings" | "reporting";
  tableFilterState: GridFilterModel;
  invoiceTableColumns: GridColDef[];
  invoiceTableHiddenColumns: GridColumnVisibilityModel;
  poLinksTableColumns: GridColDef[];
  poLinksTableHiddenColumns: GridColumnVisibilityModel;
  searchState: SearchState;
  persistedFilters: GridFilterModel;
  LGInternalUser:boolean;
}

const initialSearchState: SearchState = {
  selectedColumn: "",
  searchQuery: "",
  dropdownSelected: false,
  dateFilters: {
    invoiceDate: "",
    glDate: "",
  },
};

const savedSearchState = localStorage.getItem("persistedSearchState");
const parsedSearchState: SearchState = savedSearchState ? JSON.parse(savedSearchState) : initialSearchState;


const savedFilters = localStorage.getItem("persistedFilters");
const parsedFilters: GridFilterModel = savedFilters ? JSON.parse(savedFilters) : { items: [] };

const initialState: ActiveScreenState = {
  selectedInvoice: undefined,
  activeScreen: localStorage.getItem('expiration') === null ? "login": "invoices",
  enteredInvoiceInformation: EnteringInvoiceInformationDefaultState,
  tableFilterState: parsedFilters, //  Use persisted filters if available
  invoiceTableColumns: invoiceTableGridColumnsFromSchema,
  invoiceTableHiddenColumns: {},
  poLinksTableColumns: poLinksTableGridColumnsFromSchema,
  poLinksTableHiddenColumns: {},
  persistedFilters: parsedFilters, //  Load persisted filters
  searchState: parsedSearchState,
  LGInternalUser:false,
};

export const activeScreenSlice = createSlice({
  name: "activeScreen",
  initialState, 
  reducers: {
    openLogin: (state) => {
      state.selectedInvoice = undefined
      state.activeScreen = "login"
      state.enteredInvoiceInformation = EnteringInvoiceInformationDefaultState
    },
    openLanding: (state) => {
      state.selectedInvoice = undefined
      state.activeScreen = "landing"
      state.enteredInvoiceInformation = EnteringInvoiceInformationDefaultState
    },
    openInvoices: (state) => {
      state.selectedInvoice = undefined
      state.activeScreen = "invoices"
      state.enteredInvoiceInformation = EnteringInvoiceInformationDefaultState
    },
    openReporting: (state) => {
      state.selectedInvoice = undefined
      state.activeScreen = "reporting"
      state.enteredInvoiceInformation = EnteringInvoiceInformationDefaultState
    },
    openInvoiceDetail: (state, action: PayloadAction<number>) => {
      state.selectedInvoice = Number(action.payload)
      state.activeScreen = "invoiceDetail"
      state.enteredInvoiceInformation = EnteringInvoiceInformationDefaultState
    },
    openSettings: (state) => {
      state.activeScreen = "settings"
    },
    updateTableFilters: (state, action: PayloadAction<GridFilterModel>) => {
      state.tableFilterState = action.payload;
      state.persistedFilters = action.payload; //  Persist filter updates
      localStorage.setItem("persistedFilters", JSON.stringify(action.payload)); //  Store filters persistently
    },
    updateInvoiceTableColumns: (state, action: PayloadAction<GridColDef[]>) => {
      state.invoiceTableColumns = action.payload
    },
    updateInvoiceTableHiddenColumns: (state, action: PayloadAction<GridColumnVisibilityModel>) => {
      state.invoiceTableHiddenColumns = action.payload
    },
    updatePoLinksTableColumns: (state, action: PayloadAction<GridColDef[]>) => {
      state.poLinksTableColumns = action.payload
    },
    updatePoLinksTableHiddenColumns: (state, action: PayloadAction<GridColumnVisibilityModel>) => {
      state.poLinksTableHiddenColumns = action.payload
    },
    purchaseOrderAddLineItems: (
      state,
      action: PayloadAction<UiEditContainer[]>,
    ) => {
      state.enteredInvoiceInformation.lineItems =
        state.enteredInvoiceInformation.lineItems.concat(action.payload)
    },
    purchaseOrderCopyPasteRowData: (
      state,
      action: PayloadAction<CopyPasteArgs>,
    ) => {
      const sourceRow = state.enteredInvoiceInformation.lineItems.findIndex(
        (item) => item.temp_line_identifier === action.payload.source,
      )
      const destinationRow =
        state.enteredInvoiceInformation.lineItems.findIndex(
          (item) => item.temp_line_identifier === action.payload.destination,
        )
      if (sourceRow !== -1 && destinationRow !== -1) {
        const destinationObject =
          state.enteredInvoiceInformation.lineItems[destinationRow]
        const sourceObject =
          state.enteredInvoiceInformation.lineItems[sourceRow]
        destinationObject.non_editable_po_fields.line_type =
          sourceObject.non_editable_po_fields.line_type

        destinationObject.amount = sourceObject.amount
        destinationObject.quantity = sourceObject.quantity
        destinationObject.unit_price = sourceObject.unit_price
        destinationObject.tax_amount = sourceObject.tax_amount
        destinationObject.non_editable_po_fields.distribution_code =
          sourceObject.non_editable_po_fields.distribution_code ||
          destinationObject.non_editable_po_fields.distribution_code
        destinationObject.unit = sourceObject.unit
        destinationObject.non_editable_po_fields.shipment_number =
          sourceObject.non_editable_po_fields.shipment_number ||
          destinationObject.non_editable_po_fields.shipment_number
        destinationObject.description = sourceObject.description
      }
    },
    purchaseOrderUpdateRowSelection: (
      state,
      action: PayloadAction<GridRowSelectionModel>,
    ) => {
      state.enteredInvoiceInformation.rowSelectionModel = action.payload
      state.enteredInvoiceInformation.purchaseOrderRowSelectionCount =
        state.enteredInvoiceInformation.rowSelectionModel.length

      const selectedRows = state.enteredInvoiceInformation.lineItems.filter(
        (lineItem) =>
          lineItem.temp_line_identifier &&
          state.enteredInvoiceInformation.rowSelectionModel.includes(
            lineItem.temp_line_identifier,
          ),
      )
      let sum = selectedRows.reduce(
        (acc, curr) => acc.add(Big(curr.amount || 0)),
        Big(0),
      )

      state.enteredInvoiceInformation.purchaseOrderRowSelectionSum =
        sum.toNumber()
      state.enteredInvoiceInformation.purchaseOrderRowSelectionMismatch =
        Number(
          Big(state.enteredInvoiceInformation.totalLineAmount || 0).sub(sum),
        )
    },
    purchaseOrderUpdateLineItem: (state, action) => {
      const index = state.enteredInvoiceInformation.lineItems.findIndex(
        (item) =>
          item.temp_line_identifier === action.payload.tempLineIdentifier,
      )
      if (index !== -1) {
        // Update the item in the list with new values
        const updatedItems = [...state.enteredInvoiceInformation.lineItems]
        updatedItems[index] = {
          ...updatedItems[index],
          amount: action.payload.lineAmount,
          description: action.payload.description,
          tax_amount: action.payload.taxAmount,
          quantity: action.payload.quantity,
          unit_price: action.payload.price,
          unit: action.payload.unitOfMeasure,
          tax_classification_code: action.payload.taxCode,
        }

        updatedItems[index].non_editable_po_fields!.line_type =
          action.payload.lineType
        updatedItems[index].non_editable_po_fields!.distribution_code =
          action.payload.codeCombination
        updatedItems[index].non_editable_po_fields!.shipment_number =
          action.payload.shipmentNumber

        state.enteredInvoiceInformation.lineItems = updatedItems
      }
    },
    hydrateInvoiceDetail: (
      state,
      action: PayloadAction<EditingInvoiceState>,
    ) => {
      state.enteredInvoiceInformation = {
        ...action.payload,
        hydrated: true,
      }
    },
    updateInvoiceDetail: (
      state,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      state.enteredInvoiceInformation = {
        ...state.enteredInvoiceInformation,
        [action.payload.key]: action.payload.value,
      }
    },
    markHydrated: (state) => {
      state.enteredInvoiceInformation.hydrated = true
    },
    updateSearchState: (state, action: PayloadAction<Partial<SearchState>>) => {
      state.searchState = { ...state.searchState, ...action.payload };
      localStorage.setItem("persistedSearchState", JSON.stringify(state.searchState)); //  Persist search state
    },
    resetSearchState: (state) => {
      state.searchState = initialSearchState;
      localStorage.removeItem("persistedSearchState");
    },
    setLGInternalUser: (state, action: PayloadAction<boolean>) => {
      state.LGInternalUser = action.payload;
    },
  },
})

export const {
  openLogin,
  openLanding,
  openInvoices,
  openReporting,
  openInvoiceDetail,
  openSettings,
  purchaseOrderAddLineItems,
  purchaseOrderUpdateRowSelection,
  purchaseOrderUpdateLineItem,
  hydrateInvoiceDetail,
  updateInvoiceDetail,
  updateTableFilters,
  updateInvoiceTableColumns,
  updateInvoiceTableHiddenColumns,
  updatePoLinksTableColumns,
  updatePoLinksTableHiddenColumns,
  purchaseOrderCopyPasteRowData,
  markHydrated,
  updateSearchState,
  resetSearchState,
  setLGInternalUser
} = activeScreenSlice.actions

export const selectActivePage = (state: RootState) => state.activeScreen
export const selectEnteredInvoiceInformation = (state: RootState) =>
  state.activeScreen.enteredInvoiceInformation

export const selectTableFilters = (state: RootState) =>
  state.activeScreen.persistedFilters && state.activeScreen.persistedFilters.items.length > 0
    ? state.activeScreen.persistedFilters
    : state.activeScreen.tableFilterState;

export const selectInvoiceTableColumns = (state: RootState) =>
  state.activeScreen.invoiceTableColumns

export const selectInvoiceTableHiddenColumns = (state: RootState) =>
  state.activeScreen.invoiceTableHiddenColumns

export const selectPoLinksTableColumns = (state: RootState) =>
  state.activeScreen.poLinksTableColumns

export const selectPoLinksTableHiddenColumns = (state: RootState) =>
  state.activeScreen.poLinksTableHiddenColumns

export const selectHydrationState = (state: RootState) =>
  state.activeScreen.enteredInvoiceInformation.hydrated

export const selectOpco = (state: RootState) =>
  state.activeScreen.enteredInvoiceInformation.opcoSlug

export const selectSearchState = (state: RootState) =>
  state.activeScreen.searchState

export const selectLGInternalUser = (state: RootState) => state.activeScreen.LGInternalUser;

export default activeScreenSlice.reducer
